import * as React from "react";
import { Box, Typography } from "@mui/material";

export function ParkingMapGrid({
  name,
  rows,
  cols,
  slots,
  selectedIds,
  onChangeSelected,
  cellSize = 44,
  selectionEnabled = true, // 👈 NUEVO
}) {
  const slotByPos = React.useMemo(() => {
    const map = new Map();
    slots.forEach((s) => map.set(`${s.row}:${s.col}`, s));
    return map;
  }, [slots]);

  const selectedSet = React.useMemo(() => new Set(selectedIds), [selectedIds]);

  const toggle = React.useCallback(
    (id, multi) => {
      if (!multi) {
        onChangeSelected(selectedSet.has(id) ? [] : [id]);
        return;
      }
      const next = new Set(selectedSet);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      onChangeSelected(Array.from(next));
    },
    [onChangeSelected, selectedSet]
  );

  const isSelectable = (slot) => slot.status !== "blocked";

  return (
    <Box sx={{ display: "grid", gap: 1.5, alignContent: "start" }}>
      {/* <Typography variant="h6">{ name }</Typography> */}

      <Box
        role="grid"
        aria-label="Mapa de estacionamiento en grilla"
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${cols}, ${cellSize}px)`,
          gridTemplateRows: `repeat(${rows}, ${cellSize}px)`,
          gap: "6px",
          p: 1,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          width: "fit-content",
          userSelect: "none",
          pointerEvents: selectionEnabled ? "auto" : "none", // 👈 bloquea interacción
        }}
      >
        {Array.from({ length: rows * cols }).map((_, idx) => {
          const row = Math.floor(idx / cols);
          const col = idx % cols;
          const slot = slotByPos.get(`${row}:${col}`);

          if (!slot) {
            return (
              <Box
                key={`empty-${row}-${col}`}
                role="gridcell"
                aria-label={`Celda vacía fila ${row + 1} columna ${col + 1}`}
                sx={{
                  width: cellSize,
                  height: cellSize,
                  borderRadius: 1.5,
                  bgcolor: "action.hover",
                  opacity: 0.35,
                }}
              />
            );
          }

          const selected = selectedSet.has(slot.id);
          const disabled = !isSelectable(slot);

          return (
            <Box
              key={slot.id}
              role="gridcell"
              tabIndex={disabled ? -1 : 0}
              aria-label={`Slot ${slot.label || slot.id}, fila ${slot.row + 1}, columna ${
                slot.col + 1
              }, estado ${slot.status}${selected ? ", seleccionado" : ""}`}
              aria-selected={selected}
              onClick={(e) => {
                if (!selectionEnabled) return;
                if (disabled) return;
                const multi = e.ctrlKey || e.metaKey;
                toggle(slot.id, multi);
              }}
              onKeyDown={(e) => {
                if (!selectionEnabled) return;
                if (disabled) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  const multi = e.ctrlKey || e.metaKey;
                  toggle(slot.id, multi);
                }
              }}
              sx={{
                width: cellSize,
                height: cellSize,
                borderRadius: 1.5,
                display: "grid",
                placeItems: "center",
                border: "1px solid",
                cursor: disabled ? "not-allowed" : "pointer",
                transition: "transform 80ms ease, box-shadow 120ms ease",
                outline: "none",

                ...(slot.status === "free" && {
                  bgcolor: "lightgreen",
                  borderColor: "divider",
                }),
                ...(slot.status === "occupied" && {
                  bgcolor: "lightgray",
                  borderColor: "divider",
                }),
                ...(slot.status === "blocked" && {
                  bgcolor: "action.disabledBackground",
                  borderColor: "divider",
                  opacity: 0.6,
                }),

                ...(selected && {
                  borderColor: "primary.main",
                  boxShadow: 2,
                }),

                "&:hover": disabled
                  ? undefined
                  : {
                      transform: "translateY(-1px)",
                      boxShadow: selected ? 3 : 1,
                    },

                "&:focus-visible": disabled
                  ? undefined
                  : {
                      boxShadow: 3,
                      borderColor: "primary.main",
                    },
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {slot.label || `${slot.row + 1}-${slot.col + 1}`}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
